export default function TechnicalArchitecture() {
  const modules = [
    {
      id: 'A',
      title: 'Deno 確定性運算核心',
      subtitle: '消除 LLM 時曆幻覺的根本解法',
      description:
        '我們手寫 HTTP 指令，直接對接天文資料庫，繞過大型語言模型固有的日期運算缺陷。每一個時間節點均經過三重驗證：格里曆轉換、儒略日計算、真太陽時校正。對運算漂移的容忍度：零。',
      tech: ['Deno Runtime', 'TypeScript', 'HTTP Direct Call', 'Astronomical DB'],
      icon: 'ri-terminal-box-line',
    },
    {
      id: 'B',
      title: '自主執行流水線',
      subtitle: '100% 無人值守的生產工作流',
      description:
        '基於 Make.com 構建的自動化層，處理完整鏈路——從數據攝取、行為參數運算、報告生成到加密交付——零人工介入。每個節點均經過穩定性壓力測試，並內建自動錯誤恢復機制。交付時間：5 分鐘以內。',
      tech: ['Make.com', 'Webhook Trigger', 'Error Handling', 'Auto Retry'],
      icon: 'ri-flow-chart',
    },
    {
      id: 'C',
      title: 'AI 戰略編譯器',
      subtitle: 'Strategic Compiler',
      description:
        '摒棄人工主觀干預與模糊解讀。透過 GPT-4o 進行純邏輯的因果推論，將底層數據轉化為高階商業語彙，確保每一份報告皆具備極致的客觀性與戰略指導力。',
      tech: ['GPT-4o', 'Causal Inference', 'Business Lexicon', 'Zero Hallucination'],
      icon: 'ri-brain-line',
    },
  ];

  return (
    <section id="architecture" className="py-32 px-6 bg-[#050508] border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <div className="text-xs tracking-widest text-[#00D9FF]/60 mb-4 font-mono">
            // TECHNICAL_ARCHITECTURE
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">公開構建</h2>
          <p className="text-base text-white/40 max-w-2xl mx-auto leading-relaxed">
            透明度是信任的底層架構。以下是 TWINODE 的完整技術棧——每個模組均已在生產環境中實戰驗證，每個邏輯節點均可審計。
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {modules.map((module, index) => (
            <div
              key={module.id}
              className="border border-white/10 bg-white/[0.02] p-8 hover:border-[#00D9FF]/40 hover:bg-white/[0.04] transition-all duration-300 group cursor-pointer rounded-sm"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-10 h-10 bg-[#00D9FF]/10 border border-[#00D9FF]/30 text-[#00D9FF] flex items-center justify-center text-sm font-bold font-mono">
                  {module.id}
                </div>
                <i
                  className={`${module.icon} text-2xl text-white/10 group-hover:text-[#00D9FF]/50 transition-colors`}
                  aria-hidden="true"
                ></i>
              </div>

              <h3 className="text-lg font-bold mb-1 text-white">{module.title}</h3>
              <div className="text-xs text-[#00D9FF]/50 mb-4 font-mono tracking-wider">{module.subtitle}</div>

              <p className="text-sm text-white/45 leading-relaxed mb-6">
                {module.description}
              </p>

              <div className="pt-5 border-t border-white/8">
                <div className="text-[10px] text-white/25 mb-3 font-mono tracking-widest">TECH_STACK</div>
                <div className="flex flex-wrap gap-2">
                  {module.tech.map((tech) => (
                    <span
                      key={tech}
                      className="text-[10px] font-mono bg-white/5 border border-white/10 px-2 py-1 rounded text-white/40 whitespace-nowrap"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* System Flow Diagram */}
        <div className="mt-16 bg-white/[0.02] border border-white/8 text-white p-12 rounded-sm">
          <div className="text-center mb-12">
            <div className="text-[10px] tracking-widest text-[#00D9FF]/50 mb-4 font-mono">
              // SYSTEM_FLOW
            </div>
            <h3 className="text-2xl font-bold text-white">
              端對端自動化流水線
            </h3>
          </div>

          <div
            className="flex flex-col md:flex-row items-center justify-between gap-8"
            role="img"
            aria-label="四步驟自動化流水線：數據攝取、時空參數運算、戰略詞彙轉譯、報告生成"
          >
            {[
              { num: '1', label: '數據攝取', sub: 'Webhook 觸發' },
              { num: '2', label: '時空運算', sub: 'Deno 確定性核心' },
              { num: '3', label: '戰略轉譯', sub: 'GPT-4o 編譯器' },
              { num: '4', label: '報告交付', sub: 'PDF / 加密傳輸' },
            ].map((step, idx) => (
              <>
                <div key={step.num} className="flex-1 text-center">
                  <div className="w-14 h-14 bg-[#00D9FF]/10 border border-[#00D9FF]/30 text-[#00D9FF] flex items-center justify-center text-xl font-bold font-mono mx-auto mb-4">
                    {step.num}
                  </div>
                  <div className="font-bold text-white mb-1">{step.label}</div>
                  <div className="text-xs text-white/30 font-mono">{step.sub}</div>
                </div>
                {idx < 3 && (
                  <div
                    key={`arrow-${idx}`}
                    className="hidden md:block text-white/20 text-xl font-mono"
                    aria-hidden="true"
                  >
                    →
                  </div>
                )}
              </>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
